import React from 'react';
import { array, bool, func, number, object, shape, string } from 'prop-types';
import { compose } from 'redux';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import { propTypes } from '../../util/types';
import { sendVerificationEmail, hasCurrentUserErrors, hideNewAgreementPopup, hideProfileUpdatePopup } from '../../ducks/user.duck';
import { logout, authenticationInProgress } from '../../ducks/Auth.duck';
import { manageDisableScrolling } from '../../ducks/UI.duck';
import { Topbar } from '../../components';
import { sendMedooLoginLink } from './TopbarContainer.duck';
import { COACH_ROLE } from '../../marketplace-custom-config';
import { isUserCreatedBeforeAgreementDate } from '../../util/newAgreementHelpers';
import { LISTING_PAGE_DRAFT_VARIANT } from '../../util/urlHelpers';

// Helper function to check if new agreement popup should be shown
const shouldShowNewAgreementPopup = (currentUser, currentUserListing, currentUserListingFetched) => {
  const role = currentUser?.attributes?.profile?.publicData?.role;
  
  // Only show for coaches
  if (role !== COACH_ROLE) {
    return false;
  }
  
  // Only show if listing has been fetched and listing status is not draft
  if (!currentUserListingFetched || !currentUserListing) {
    return false;
  }
  if (currentUserListing?.attributes?.state === LISTING_PAGE_DRAFT_VARIANT) {
    return false;
  }

  // Check if user has already signed the new agreement
  const {newAgreementSignedDate} = currentUserListing?.attributes?.publicData?.agreementDetails || {};
  if (newAgreementSignedDate) {
    return false;
  }
  return isUserCreatedBeforeAgreementDate(currentUser);
};

// Helper function to check if profile update popup should be shown
const shouldShowProfileUpdatePopup = (currentUser, currentUserListing, currentUserListingFetched) => {
  const { role } = currentUser?.attributes?.profile?.publicData || {};

  // Only show for coaches
  if (role !== COACH_ROLE) {
    return false;
  }

  // Only show if listing has been fetched and exists
  if (!currentUserListingFetched || !currentUserListing) {
    return false;
  }

  const { state, createdAt, publicData = {} } = currentUserListing?.attributes || {};

  // Only show for published listings (not drafts)
  if (state === LISTING_PAGE_DRAFT_VARIANT) {
    return false;
  }

  // Only show if listing was created before the profile update prompt date
  const profileUpdatePromptDateStr = process.env.REACT_APP_PROFILE_UPDATE_PROMPT_DATE;
  if (!profileUpdatePromptDateStr) {
    return false;
  }

  const cutoffTimestamp = new Date(profileUpdatePromptDateStr).getTime();
  const createdAtTimestamp = createdAt ? new Date(createdAt).getTime() : NaN;

  if (Number.isNaN(cutoffTimestamp) || Number.isNaN(createdAtTimestamp)) {
    return false;
  }

  if (createdAtTimestamp >= cutoffTimestamp) {
    return false;
  }

  // Don't show if coach has already updated their listing after the announcement
  const { profileUpdatedAfterAnnouncement } = publicData;
  if (profileUpdatedAfterAnnouncement) {
    return false;
  }

  return true;
};

export const TopbarContainerComponent = props => {
  const {
    authInProgress,
    currentPage,
    currentSearchParams,
    currentUser,
    currentUserHasListings,
    currentUserListing,
    currentUserListingFetched,
    currentUserHasOrders,
    history,
    isAuthenticated,
    authScopes,
    hasGenericError,
    location,
    notificationCount,
    onLogout,
    onManageDisableScrolling,
    sendVerificationEmailInProgress,
    sendVerificationEmailError,
    onResendVerificationEmail,
    hideSearchbar,
    ...rest
  } = props;

  return (
    <Topbar
      authInProgress={authInProgress}
      currentPage={currentPage}
      currentSearchParams={currentSearchParams}
      currentUser={currentUser}
      currentUserHasListings={currentUserHasListings}
      currentUserListing={currentUserListing}
      currentUserListingFetched={currentUserListingFetched}
      currentUserHasOrders={currentUserHasOrders}
      history={history}
      isAuthenticated={isAuthenticated}
      authScopes={authScopes}
      location={location}
      notificationCount={notificationCount}
      onLogout={onLogout}
      onManageDisableScrolling={onManageDisableScrolling}
      onResendVerificationEmail={onResendVerificationEmail}
      sendVerificationEmailInProgress={sendVerificationEmailInProgress}
      sendVerificationEmailError={sendVerificationEmailError}
      showGenericError={hasGenericError}
      hideSearchbar={hideSearchbar}
      {...rest}
    />
  );
};

TopbarContainerComponent.defaultProps = {
  currentPage: null,
  currentSearchParams: null,
  currentUser: null,
  currentUserHasOrders: null,
  notificationCount: 0,
  sendVerificationEmailError: null,
  currentUserListing: null,
  authScopes: null,
  hideSearchbar: false,
  handleMoveToCoachingCultureSection: () => {},
  accessibleEngagementList: [],
  sendLoginLinkInProgress: false,
  sendLoginLinkError: null,
};

TopbarContainerComponent.propTypes = {
  authInProgress: bool.isRequired,
  currentPage: string,
  currentSearchParams: object,
  currentUser: propTypes.currentUser,
  currentUserHasListings: bool.isRequired,
  currentUserListingFetched: bool.isRequired,
  currentUserListing: propTypes.ownListing,
  currentUserHasOrders: bool,
  isAuthenticated: bool.isRequired,
  authScopes: array,
  notificationCount: number,
  onLogout: func.isRequired,
  onManageDisableScrolling: func.isRequired,
  sendVerificationEmailInProgress: bool.isRequired,
  sendVerificationEmailError: propTypes.error,
  onResendVerificationEmail: func.isRequired,
  hasGenericError: bool.isRequired,
  hideSearchbar: bool,
  handleMoveToCoachingCultureSection: func,
  accessibleEngagementList: array,
  sendLoginLinkInProgress: bool, 
  sendLoginLinkError: propTypes.error,
  onSendMedooLoginLink: func.isRequired,

  // from withRouter
  history: shape({
    push: func.isRequired,
  }).isRequired,
  location: shape({ state: object }).isRequired,
};

const mapStateToProps = state => {
  // Topbar needs isAuthenticated
  const { isAuthenticated, logoutError, authScopes, justLoggedIn } = state.Auth;
  // Topbar needs user info.
  const {
    currentUser,
    currentUserHasListings,
    currentUserListing,
    currentUserListingFetched,
    currentUserHasOrders,
    currentUserNotificationCount: notificationCount,
    sendVerificationEmailInProgress,
    sendVerificationEmailError,
    accessibleEngagementList,
  } = state.user;
  const { sendLoginLinkInProgress, sendLoginLinkError } = state.TopbarContainer;
  const hasGenericError = !!(logoutError || hasCurrentUserErrors(state));
  
  // Compute showNewAgreementPopup based on current user state
  const showNewAgreementPopup = currentUser && !state.user.newAgreementPopupClosed
    ? shouldShowNewAgreementPopup(currentUser, currentUserListing, currentUserListingFetched)
    : false;

  // Compute showProfileUpdatePopup based on current user state
  const showProfileUpdatePopup = currentUser && !state.user.profileUpdatePopupClosed
    ? shouldShowProfileUpdatePopup(currentUser, currentUserListing, currentUserListingFetched)
    : false;
  
  return {
    authInProgress: authenticationInProgress(state),
    currentUser,
    currentUserHasListings,
    currentUserListing,
    currentUserListingFetched,
    currentUserHasOrders,
    notificationCount,
    isAuthenticated,
    authScopes,
    sendVerificationEmailInProgress,
    sendVerificationEmailError,
    hasGenericError,
    accessibleEngagementList,
    sendLoginLinkInProgress,
    sendLoginLinkError,
    showNewAgreementPopup,
    showProfileUpdatePopup: justLoggedIn && showProfileUpdatePopup,
  };
};

const mapDispatchToProps = dispatch => ({
  onLogout: historyPush => dispatch(logout(historyPush)),
  onManageDisableScrolling: (componentId, disableScrolling) =>
    dispatch(manageDisableScrolling(componentId, disableScrolling)),
  onResendVerificationEmail: () => dispatch(sendVerificationEmail()),
  onSendMedooLoginLink: () => dispatch(sendMedooLoginLink()),
  onCloseNewAgreementPopup: () => dispatch(hideNewAgreementPopup()),
  onCloseProfileUpdatePopup: () => dispatch(hideProfileUpdatePopup()),
});

// Note: it is important that the withRouter HOC is **outside** the
// connect HOC, otherwise React Router won't rerender any Route
// components since connect implements a shouldComponentUpdate
// lifecycle hook.
//
// See: https://github.com/ReactTraining/react-router/issues/4671
const TopbarContainer = compose(
  withRouter,
  connect(
    mapStateToProps,
    mapDispatchToProps
  )
)(TopbarContainerComponent);

export default TopbarContainer;
